// Paint a commit's diff onto whatever model the streaming viewer has
// loaded: the changed elements keep a colour that says what happened to
// them, everything else drops back to the viewer's ghosted bucket so the
// change reads on its own without losing the context it sits in.
//
// Used by two surfaces that must agree on what "the change" looks like:
//   - the issue resolve page's 3D pane (the fix a reviewer is judging)
//   - the model viewer opened with ?diff=<commitId> (a whole revision
//     waiting for approval in the Review Center)
//
// Ghosting, not hiding, is deliberate: a reviewer needs to see the wall
// that moved AND the walls it now meets. The viewer's ghost bucket
// renders as a desaturated grey tint (InstanceStateApplicator) rather
// than true alpha - per-instance transparency would need a material
// variant in the instance pool, which is a bigger change than this.
export const CHANGE_COLORS = {
  added: "#4FC92A",     // green
  modified: "#E0A43A",  // yellow / amber
  removed: "#FB8080",   // red
  // "this moved" and "this was reshaped" are different findings, and a
  // reviewer reading one as the other goes looking for the wrong change.
  // The backend now reports placementChanged on its own axis, so it gets
  // its own colour instead of being folded into modified.
  moved: "#5B8DEF",     // blue
};

export const CHANGE_LABELS = {
  added: "added",
  removed: "deleted",
  modified: "edited",
  moved: "moved",
};

// Tiles stream in and out as the camera moves, so an element that
// arrives after the overlay started must be ghosted too - otherwise the
// model slowly repaints itself solid around the change.
const GHOST_SWEEP_MS = 1500;

// viewer: the WebGlApp's `viewer` (WebGL.current.viewer)
// elements: [{ uniqueId, change: 'added'|'modified'|'removed' }]
// changedTilesByCategory: from the diff, used as a load-order hint
//
// Returns a cleanup function, or null when there is nothing to paint.
export function applyDiffOverlay(viewer, elements, changedTilesByCategory) {
  const store = viewer && (viewer.streaming?.state || viewer.stateStore);
  if (!store || typeof store.bulkHighlight !== "function") return null;

  const entries = [];
  const changed = new Set();
  for (const e of elements || []) {
    if (!e || !e.uniqueId) continue;
    // A removed element is absent from the NEW model, so in a
    // new-model-only view there is nothing to colour and it stays in the
    // reviewer's list. But when the baseline is loaded underneath - the
    // combined old+new view - the deleted element IS on screen, and
    // leaving it uncoloured is why replacing a window read as "both
    // windows are here" with no clue which was which. Paint it red.
    entries.push([e.uniqueId, CHANGE_COLORS[e.change] || CHANGE_COLORS.modified]);
    changed.add(e.uniqueId);
  }
  if (entries.length === 0) return null;

  store.bulkHighlight(entries);

  // A changed element must be on screen whatever the exporter thought of
  // it. Plan-view families (the 2D chair symbols on the rehearsal model)
  // arrive flagged disabled-by-exporter and the viewer hides those unless
  // a user reveals them - so a moved chair was in the change list but not
  // in the pane. Reveal every changed element for as long as the overlay
  // is on; the cleanup hides them again.
  if (typeof store.bulkShowDisabled === "function") store.bulkShowDisabled([...changed]);

  // And it must be LOADED whatever its size. Since 2026-09-17 the viewer's
  // triangle / draw-call budget reaches into the view (streamingController
  // _rebalanceTriangleBudget): on a whole-model view the smallest in-view
  // pieces wait until the user zooms in. A changed element is exactly the
  // piece a reviewer is looking for, so it is pinned - pinned groups are
  // never floored - for as long as the overlay is on.
  const streamingForPins = viewer.streaming;
  const pinned = streamingForPins && typeof streamingForPins.setPinnedUids === "function";
  if (pinned) {
    try { streamingForPins.setPinnedUids([...changed]); } catch (err) { /* best-effort */ }
  }

  const ghostSweep = () => {
    try {
      const streaming = viewer.streaming;
      const uids = streaming && typeof streaming.enumerateActiveUids === "function"
        ? streaming.enumerateActiveUids()
        : [];
      const ghost = [];
      for (const uid of uids) if (!changed.has(uid)) ghost.push(uid);
      if (ghost.length && typeof store.bulkDisable === "function") store.bulkDisable(ghost);
    } catch (err) {
      // An overlay must never take the viewer down with it.
    }
  };
  ghostSweep();
  const timer = setInterval(ghostSweep, GHOST_SWEEP_MS);

  // Load the tiles carrying the change first.
  try {
    const tiles = [];
    Object.values(changedTilesByCategory || {}).forEach((list) =>
      (list || []).forEach((t) => { if (t) tiles.push(t); })
    );
    const streaming = viewer.streaming;
    if (tiles.length && streaming && typeof streaming.prefetchTiles === "function") {
      streaming.prefetchTiles(tiles);
    }
  } catch (err) { /* best-effort */ }

  return () => {
    clearInterval(timer);
    if (pinned) {
      try { streamingForPins.setPinnedUids([]); } catch (err) { /* best-effort */ }
    }
    try {
      if (typeof store.clearDisabled === "function") store.clearDisabled();
      if (typeof store.bulkClearHighlights === "function") store.bulkClearHighlights([...changed]);
      if (typeof store.bulkHideDisabled === "function") store.bulkHideDisabled([...changed]);
      const streaming = viewer.streaming;
      if (streaming && typeof streaming.clearPrefetch === "function") streaming.clearPrefetch();
    } catch (err) { /* best-effort */ }
  };
}

// Counts for a legend, from the diff's summary.
// One flat, sorted element list from a CommitDiff - the thing a reviewer
// actually reads. `moved` is split out of `modified` using the
// placementChanged flag the diff already computes, so the four buckets
// are the four questions people ask: what appeared, what went, what was
// reshaped, what merely relocated.
export function diffElementRows(diff) {
  const d = diff || {};
  const rows = [];
  const push = (e, change, extra = {}) => {
    if (!e) return;
    rows.push({
      ...extra,
      uniqueId: e.uniqueId || "",
      change,
      category: e.category || "",
      family: e.family || "",
      // The endpoint calls it typeName, the cache row calls it name.
      type: e.type || e.typeName || e.name || "",
      level: e.level || "",
      tileKey: e.tileKey || "",
      // Which axis actually changed - shown as chips so "edited" is not
      // a dead end.
      why: [
        e.fragmentHashChanged ? "geometry" : "",
        e.paramHashChanged ? "parameters" : "",
        e.placementChanged ? "placement" : "",
      ].filter(Boolean),
    });
  };
  // TWO SHAPES REACH THIS FUNCTION, and reading only one is why the
  // panel showed "Elements (0)" next to "Fragments (3)":
  //
  //   GET /commits/:id/diff returns a FLAT `elements` array, each row
  //   carrying its own `change` - that is what the viewer and the
  //   drawer are given.
  //
  //   The CommitDiff document itself is BUCKETED (added/modified/
  //   removed) - that is what anything reading the cache row hands over.
  //
  // Only the bucketed shape was handled, so every consumer of the
  // endpoint got an empty list while the tile tab - which reads
  // changedTilesByCategory, present in both - filled in normally. The
  // counts all read 0 for the same reason.
  if (Array.isArray(d.elements) && d.elements.length) {
    d.elements.forEach((e) => {
      if (!e) return;
      if (e.change === "removed") return push(e, "removed", { inferred: true });
      if (e.change === "modified") {
        const onlyMoved = e.placementChanged && !e.fragmentHashChanged && !e.paramHashChanged;
        return push(e, onlyMoved ? "moved" : "modified");
      }
      return push(e, e.change || "modified");
    });
    return sortRows(rows);
  }

  (d.added || []).forEach((e) => push(e, "added"));
  // A deletion is INFERRED, never reported: Revit does not say "this was
  // deleted", so the diff derives it from an element being in the
  // baseline and absent here. Anything that keeps an element out of an
  // export therefore looks identical to a deletion - including a
  // geometry-extraction failure. Rows are flagged so the panel can say
  // "not in this export" rather than assert a demolition that may not
  // have happened.
  (d.removed || []).forEach((e) => push(e, "removed", { inferred: true }));
  (d.modified || []).forEach((e) => {
    // Placement alone -> moved. Anything else -> edited.
    const onlyMoved = e.placementChanged && !e.fragmentHashChanged && !e.paramHashChanged;
    push(e, onlyMoved ? "moved" : "modified");
  });
  return sortRows(rows);
}

function sortRows(rows) {
  const order = { added: 0, removed: 1, modified: 2, moved: 3 };
  rows.sort((a, b) => (order[a.change] - order[b.change])
    || String(a.category).localeCompare(String(b.category))
    || String(a.family).localeCompare(String(b.family)));
  return rows;
}

// Every fragment/tile the change touched, grouped by category - the
// "what actually has to re-stream" view.
export function diffTileRows(diff) {
  const byCat = (diff && diff.changedTilesByCategory) || {};
  return Object.entries(byCat)
    .map(([category, tiles]) => ({
      category,
      tiles: Array.isArray(tiles) ? tiles : [],
      count: Array.isArray(tiles) ? tiles.length : 0,
    }))
    .sort((a, b) => b.count - a.count || a.category.localeCompare(b.category));
}

// How much of a diff the element list actually holds.
//
// The counts in `summary` are exact. The element LIST is not: the server
// returns at most `limit` rows (2,000), and a diff too large to store is
// capped per kind (routes/commits.js MAX_STORED_DIFF_ROWS, then trimmed to
// fit one MongoDB document). On SpaceX 6aa649fe that is 64,874 added /
// 145,746 removed / 72,948 modified against a list of a few thousand, and
// nothing on screen said so - a reviewer could read the list as the change.
//   -> { shown, total, capped }
export function diffCoverage(diff) {
  const s = (diff && diff.summary) || {};
  const total = (s.added || 0) + (s.modified || 0) + (s.removed || 0);
  const shown = ((diff && diff.elements) || []).length;
  return { shown, total, capped: total > shown };
}

// One sentence for the reviewer when the list is partial, or "".
export function diffCoverageNote(diff) {
  const { shown, total, capped } = diffCoverage(diff);
  if (!capped) return "";
  const n = (v) => Number(v).toLocaleString("en-US");
  return `Showing ${n(shown)} of ${n(total)} changed elements. The counts are exact; the list is a sample - filter by category or open a smaller push to see the rest.`;
}

export function diffLegendRows(summary) {
  const s = summary || {};
  return [
    { key: "added", label: "added", count: s.added || 0, color: CHANGE_COLORS.added },
    { key: "modified", label: "modified", count: s.modified || 0, color: CHANGE_COLORS.modified },
    { key: "removed", label: "deleted", count: s.removed || 0, color: CHANGE_COLORS.removed },
  ];
}
